import * as Sentry from '@sentry/react';
import { kebabCase } from 'lodash-es';
import type { ReactNode } from 'react';
import type { FloorPlanTablesRenderer } from './FloorPlan';
import { type TableHighlightingMode } from './FloorPlanTable';
import { InteractiveFloorPlanTable } from './InteractiveFloorPlanTable';
import { NonInteractiveFloorPlanTable } from './NonInteractiveFloorPlanTable';

export interface RendererFloorPlanTable {
  iconName: string;
  iconWidthScale: string | number;
  left: string | number;
  name: string;
  orientation: string | number;
  pulse?: boolean;
  top: string | number;
}

export interface InteractiveFloorPlanTablesRendererFactoryProps<
  T extends RendererFloorPlanTable,
> {
  showName?: boolean;
  calculateIsComboHovered?: (floorPlanTable: T) => boolean;
  calculateIsHighlighted?: (floorPlanTable: T) => boolean;
  calculateIsSelectable?: (floorPlanTable: T) => boolean;
  calculateIsUnused?: (floorPlanTable: T) => boolean;
  errorState?: boolean;
  handleTableOnClick?: (floorPlanTable: T) => void;
  handleTableOnMouseEnter?: (floorPlanTable: T) => void;
  handleTableOnMouseLeave?: (floorPlanTable: T) => void;
  tables: T[];
  unitLabel?: string;
  unselectableTooltipText?: string;
  highlightIcon?: boolean;
}

// TODO: Temporary logging to identify Sentry Event (Dec 13th 2024)
// https://peak-reservations-llc.sentry.io/issues/6134993890/events/cf35238c73644d37ad92345752f09146/?project=5972755
const reportMissingFloorPlanTableIconNameToSentry = <T extends object>(
  table: T,
) =>
  Sentry.captureException(
    new Error(
      `Cannot render floor plan table without an icon name: ${JSON.stringify(table)}`,
    ),
  );

export const interactiveFloorPlanTablesRendererFactory =
  <T extends RendererFloorPlanTable>({
    showName = false,
    calculateIsComboHovered = () => false,
    calculateIsHighlighted = () => false,
    calculateIsSelectable = () => true,
    calculateIsUnused = () => false,
    errorState,
    handleTableOnClick = (): void => undefined,
    handleTableOnMouseEnter,
    handleTableOnMouseLeave,
    tables,
    unitLabel,
    unselectableTooltipText,
    highlightIcon,
  }: InteractiveFloorPlanTablesRendererFactoryProps<T>): FloorPlanTablesRenderer =>
  // eslint-disable-next-line react/display-name
  (tableIconScale: number): ReactNode => (
    <>
      {tables.map((floorPlanTable) => {
        // TODO: Temporary logging to identify Sentry Event (Dec 13th 2024)
        // https://peak-reservations-llc.sentry.io/issues/6134993890/events/cf35238c73644d37ad92345752f09146/?project=5972755
        if (!floorPlanTable.iconName) {
          reportMissingFloorPlanTableIconNameToSentry(floorPlanTable);
        }
        return (
          <InteractiveFloorPlanTable
            showName={showName}
            errorState={errorState}
            handleTableOnClick={() => handleTableOnClick(floorPlanTable)}
            handleTableOnMouseEnter={
              handleTableOnMouseEnter
                ? () => handleTableOnMouseEnter(floorPlanTable)
                : undefined
            }
            handleTableOnMouseLeave={
              handleTableOnMouseLeave
                ? () => handleTableOnMouseLeave(floorPlanTable)
                : undefined
            }
            iconName={floorPlanTable.iconName}
            iconWidthScale={floorPlanTable.iconWidthScale.toString()}
            isComboHovered={calculateIsComboHovered(floorPlanTable)}
            isHighlighted={calculateIsHighlighted(floorPlanTable)}
            isSelectable={calculateIsSelectable(floorPlanTable)}
            isUnused={calculateIsUnused(floorPlanTable)}
            key={JSON.stringify(floorPlanTable)}
            left={floorPlanTable.left.toString()}
            name={floorPlanTable.name.toString()}
            orientation={floorPlanTable.orientation.toString()}
            pulse={floorPlanTable.pulse}
            tableIconScale={tableIconScale}
            testId={kebabCase(`floor-plan-table-${floorPlanTable.name}`)}
            top={floorPlanTable.top.toString()}
            unitLabel={unitLabel}
            unselectableTooltipText={unselectableTooltipText}
            highlightIcon={highlightIcon}
          />
        );
      })}
    </>
  );

export interface FloorPlanTablesRendererFactoryProps<
  T extends RendererFloorPlanTable,
> {
  dimNonHighlightedTables?: boolean;
  calculateIsHighlighted?: (floorPlanTable: T) => boolean;
  tables: T[];
  tableHighlightingMode?: TableHighlightingMode;
  showHoverStroke?: boolean;
}

export const floorPlanTablesRendererFactory =
  <T extends RendererFloorPlanTable>({
    calculateIsHighlighted = () => false,
    dimNonHighlightedTables = false,
    tables,
    tableHighlightingMode = 'outline',
    showHoverStroke,
  }: FloorPlanTablesRendererFactoryProps<T>): FloorPlanTablesRenderer =>
  // eslint-disable-next-line react/display-name
  (tableIconScale: number): ReactNode => (
    <>
      {tables.map((floorPlanTable) => (
        <NonInteractiveFloorPlanTable
          iconName={floorPlanTable.iconName}
          iconWidthScale={floorPlanTable.iconWidthScale.toString()}
          isDimmed={
            dimNonHighlightedTables && !calculateIsHighlighted(floorPlanTable)
          }
          isHighlighted={calculateIsHighlighted(floorPlanTable)}
          key={JSON.stringify(floorPlanTable)}
          left={floorPlanTable.left.toString()}
          name={floorPlanTable.name}
          orientation={floorPlanTable.orientation.toString()}
          tableIconScale={tableIconScale}
          testId={kebabCase(`floor-plan-table-${floorPlanTable.name}`)}
          top={floorPlanTable.top.toString()}
          tableHighlightingMode={tableHighlightingMode}
          showHoverStroke={showHoverStroke}
        />
      ))}
    </>
  );
